import React, { useEffect, useMemo, useState } from "react";
import { getTrainingFull } from "../../services/authService";

const DAY_NAMES_FULL = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const RPE_VALUES = [null, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10];

// Modale de paramétrage de progression pour UNE cellule (Semaine × Jour) du programme.
// Charge le template pour lister ses exos, permet de définir un mode charge par exo,
// puis les valeurs (% ou poids fixe, reps cible, rpe cible) par série. Sortie : liste
// plate de serieParams pour cette cellule uniquement — le parent merge dans l'état
// global du programme.
export default function ProgramCellProgressionPanel({
	cell,               // { semaine, jour, idTemplate, templateName }
	coachId,            // pour appeler getTrainingFull sur les templates du coach
	existingParams,     // params déjà sauvegardés pour cette cellule (peut être vide)
	casesMemeModele = [], // autres cases du même modèle : [{ semaine, jour, reglee }]
	onSave,             // (paramsForCell[], cibles[{ semaine, jour }]) => void
	onClose,
	onNotif,
}) {
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [templateExos, setTemplateExos] = useState([]);

	// État édité, structuré par exo puis par série pour matcher l'UI.
	// exos : [{ idReferentielExercice, ordreExercice, nom, mode, series: [{ pct, poidsFixe, repsCible, rpeCible }] }]
	const [exos, setExos] = useState([]);
	// Où appliquer le réglage : "case" (cette case), "suivantes" (même jour,
	// semaines suivantes) ou "toutes" (toutes les cases de ce modèle).
	const [portee, setPortee] = useState("case");

	useEffect(() => {
		if (!cell?.idTemplate || !coachId) return;
		let cancelled = false;
		setLoading(true);
		setError("");
		getTrainingFull(coachId, cell.idTemplate).then((res) => {
			if (cancelled) return;
			if (!res.success) {
				setError("Impossible de charger le modèle");
				setLoading(false);
				return;
			}
			const tplExos = (res.training?.exercices || []).filter((e) => e.supprime !== 1);
			setTemplateExos(tplExos);

			// Construit l'état initial des exos à partir du template + paramétrage existant.
			// Un exo hérite du mode "fixe" (défaut) si aucun param, sinon du mode du premier
			// param trouvé pour cet exo (cohérent avec la contrainte "un mode par exo").
			const paramsByExoOrdre = new Map();
			(existingParams || []).forEach((p) => {
				const key = `${p.ordreExercice}`;
				if (!paramsByExoOrdre.has(key)) paramsByExoOrdre.set(key, []);
				paramsByExoOrdre.get(key).push(p);
			});

			const built = tplExos.map((ex, exIdx) => {
				const existing = paramsByExoOrdre.get(`${exIdx}`) || [];
				const mode = existing[0]?.modeCharge === "pct" ? "pct" : "fixe";
				const templateSeries = ex.series || [];
				const series = templateSeries.map((tSerie, sIdx) => {
					const p = existing.find((x) => x.ordreSerie === sIdx);
					return {
						pct: p?.pctMax != null ? Math.round(p.pctMax * 100) : 75,
						poidsFixe: p?.poidsFixe != null ? p.poidsFixe : (tSerie.poids || 0),
						repsCible: p?.repsCible ?? (tSerie.reps || 0),
						rpeCible: p?.rpeCible ?? "",
					};
				});
				return {
					idReferentielExercice: ex.idExercice,
					ordreExercice: exIdx,
					nom: ex.nom || "Exercice",
					mode,
					series,
				};
			});
			setExos(built);
			setLoading(false);
		}).catch(() => {
			if (cancelled) return;
			setError("Erreur réseau");
			setLoading(false);
		});
		return () => { cancelled = true; };
	}, [cell?.idTemplate, coachId, existingParams]);

	const setExoMode = (exIdx, mode) => {
		setExos((prev) => prev.map((e, i) => i === exIdx ? { ...e, mode } : e));
	};
	const setSerieField = (exIdx, sIdx, field, value) => {
		setExos((prev) => prev.map((e, i) => {
			if (i !== exIdx) return e;
			return {
				...e,
				series: e.series.map((s, j) => j === sIdx ? { ...s, [field]: value } : s),
			};
		}));
	};

	// Raccourci "appliquer à toutes les séries" : propage 1 valeur (charge + reps
	// + rpe) sur toutes les séries de l'exo. Utile pour un préréglage rapide,
	// laisse ensuite le coach affiner série par série.
	const applyToAll = (exIdx, snapshot) => {
		setExos((prev) => prev.map((e, i) => {
			if (i !== exIdx) return e;
			return {
				...e,
				series: e.series.map((s) => ({
					...s,
					...snapshot,
				})),
			};
		}));
	};

	const handleSave = () => {
		// Flatten en paramètres API : une entrée par série, semaine/jour fixés à la cellule.
		const params = [];
		exos.forEach((e) => {
			e.series.forEach((s, sIdx) => {
				const base = {
					semaine: cell.semaine,
					jour: cell.jour,
					idReferentielExercice: e.idReferentielExercice,
					ordreExercice: e.ordreExercice,
					ordreSerie: sIdx,
					modeCharge: e.mode,
					repsCible: parseInt(s.repsCible) || 0,
					rpeCible: s.rpeCible || null,
				};
				if (e.mode === "pct") {
					const pct = parseFloat(s.pct);
					if (isNaN(pct) || pct <= 0) return; // skip séries incomplètes en mode pct
					base.pctMax = pct / 100;
					base.poidsFixe = null;
				} else {
					const p = parseFloat(s.poidsFixe);
					base.pctMax = null;
					base.poidsFixe = isNaN(p) ? 0 : p;
				}
				params.push(base);
			});
		});
		onSave?.(params, cibles.map(({ semaine, jour }) => ({ semaine, jour })));
		onNotif?.(
			cibles.length > 0
				? `Progression enregistrée sur ${cibles.length + 1} cases`
				: "Progression enregistrée sur cette cellule"
		);
		onClose?.();
	};

	const dayName = DAY_NAMES_FULL[(cell?.jour || 1) - 1] || "";
	const suivantes = casesMemeModele.filter((c) => c.jour === cell?.jour && c.semaine > cell?.semaine);
	const cibles = portee === "suivantes" ? suivantes : portee === "toutes" ? casesMemeModele : [];
	const remplacees = cibles.filter((c) => c.reglee).length;
	const pluriel = (n) => (n > 1 ? "s" : "");
	const totalSeries = useMemo(() => exos.reduce((s, e) => s + e.series.length, 0), [exos]);

	return (
		<div className="mens-modal-overlay" onClick={onClose}>
			<div
				className="mens-modal cell-prog-modal"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="mens-modal-header cell-prog-modal-header">
					<h3>
						Paramétrer la progression
						<span className="cell-prog-modal-sub"> · {cell?.templateName || "Séance"}</span>
					</h3>
					<button className="mens-modal-close" onClick={onClose}>×</button>
				</div>

				<div className="cell-prog-modal-cellchip">
					<span className="cell-prog-modal-cellchip-slot">S{cell?.semaine} · {dayName}</span>
					<span className="cell-prog-modal-cellchip-tpl">{cell?.templateName}</span>
					<span className="cell-prog-modal-cellchip-count">
						{exos.length} exo{exos.length > 1 ? "s" : ""} · {totalSeries} série{totalSeries > 1 ? "s" : ""}
					</span>
				</div>

				{/* Une charge à 0 n'impose rien à l'affectation (API, ChargeProgrammee) :
				    sans cette ligne, le 0 affiché partout se lit comme une consigne. */}
				<p className="cell-prog-modal-note">
					Charge laissée à 0 : l'élève reprend sa dernière charge sur l'exercice.
				</p>

				<div className="cell-prog-modal-scroll">
					{loading && <div className="cell-prog-modal-empty">Chargement du modèle…</div>}
					{error && <div className="mens-modal-error">{error}</div>}
					{!loading && !error && exos.length === 0 && (
						<div className="cell-prog-modal-empty">Ce modèle ne contient aucun exercice.</div>
					)}
					{!loading && exos.map((exo, exIdx) => (
						<ExoBlock
							key={`${exo.idReferentielExercice}-${exIdx}`}
							exo={exo}
							onModeChange={(m) => setExoMode(exIdx, m)}
							onSerieField={(sIdx, field, v) => setSerieField(exIdx, sIdx, field, v)}
							onApplyAll={(snap) => applyToAll(exIdx, snap)}
						/>
					))}
				</div>

				{/* Le même modèle revient souvent chaque semaine : son réglage se
				    copie au lieu d'être ressaisi case par case. */}
				{casesMemeModele.length > 0 && (
					<fieldset className="cell-prog-copie">
						<legend>Appliquer ce réglage à</legend>
						<label>
							<input type="radio" name="cell-prog-copie" checked={portee === "case"} onChange={() => setPortee("case")} />
							cette case seulement
						</label>
						{/* Même liste que « toutes » : l'option ferait doublon. */}
						{suivantes.length > 0 && suivantes.length < casesMemeModele.length && (
							<label>
								<input type="radio" name="cell-prog-copie" checked={portee === "suivantes"} onChange={() => setPortee("suivantes")} />
								le {dayName.toLowerCase()} des semaines suivantes ({suivantes.length + 1} cases)
							</label>
						)}
						<label>
							<input type="radio" name="cell-prog-copie" checked={portee === "toutes"} onChange={() => setPortee("toutes")} />
							toutes les cases « {cell?.templateName} » ({casesMemeModele.length + 1} cases)
						</label>
						{remplacees > 0 && (
							<span className="cell-prog-copie-avert">
								{remplacees} case{pluriel(remplacees)} déjà réglée{pluriel(remplacees)} : {remplacees > 1 ? "leur" : "son"} réglage sera remplacé.
							</span>
						)}
					</fieldset>
				)}
				<div className="mens-modal-actions cell-prog-modal-actions">
					<button className="mens-btn ghost" onClick={onClose}>Annuler</button>
					<button className="mens-btn primary" onClick={handleSave} disabled={loading || !!error}>
						Enregistrer la progression
					</button>
				</div>
			</div>
		</div>
	);
}

// ─── Bloc d'un exercice dans le panneau ─────────────────
function ExoBlock({ exo, onModeChange, onSerieField, onApplyAll }) {
	const [applyPct, setApplyPct] = useState(75);
	const [applyFixe, setApplyFixe] = useState(40);
	const [applyReps, setApplyReps] = useState(5);
	const [applyRpe, setApplyRpe] = useState("");

	const handleApply = () => {
		const snap = {
			repsCible: applyReps,
			rpeCible: applyRpe || "",
		};
		if (exo.mode === "pct") snap.pct = applyPct;
		else snap.poidsFixe = applyFixe;
		onApplyAll(snap);
	};

	return (
		<div className="cell-prog-exo">
			<div className="cell-prog-exo-head">
				<div className="cell-prog-exo-name">{exo.nom}</div>
				<div className="cell-prog-exo-mode">
					<button
						type="button"
						className={exo.mode === "pct" ? "active" : ""}
						onClick={() => onModeChange("pct")}
					>% du max</button>
					<button
						type="button"
						className={exo.mode === "fixe" ? "active" : ""}
						onClick={() => onModeChange("fixe")}
					>Poids fixe</button>
				</div>
			</div>

			<div className="cell-prog-exo-apply">
				<span className="cell-prog-exo-apply-label">Appliquer à toutes les séries :</span>
				<label className="cell-prog-exo-apply-field">
					<input
						type="number"
						min={0}
						max={99}
						value={applyReps}
						onChange={(e) => setApplyReps(e.target.value)}
					/>
					<span className="cell-prog-exo-apply-unit">reps</span>
				</label>
				{exo.mode === "pct" ? (
					<label className="cell-prog-exo-apply-field">
						<input
							type="number"
							min={0}
							max={200}
							step={2.5}
							value={applyPct}
							onChange={(e) => setApplyPct(e.target.value)}
						/>
						<span className="cell-prog-exo-apply-unit">%</span>
					</label>
				) : (
					<label className="cell-prog-exo-apply-field">
						<input
							type="number"
							min={0}
							step={2.5}
							value={applyFixe}
							onChange={(e) => setApplyFixe(e.target.value)}
						/>
						<span className="cell-prog-exo-apply-unit">kg</span>
					</label>
				)}
				<select
					className="cell-prog-exo-apply-select"
					value={applyRpe}
					onChange={(e) => setApplyRpe(e.target.value)}
					title="RPE cible"
				>
					<option value="">RPE —</option>
					{RPE_VALUES.filter((v) => v != null).map((v) => (
						<option key={v} value={String(v)}>RPE {v}</option>
					))}
				</select>
				<button type="button" className="cell-prog-exo-apply-btn" onClick={handleApply}>
					Appliquer
				</button>
			</div>

			<table className="cell-prog-serie-table">
				<thead>
					<tr>
						<th style={{ width: 42 }}>Sér.</th>
						<th>Reps cible</th>
						<th>{exo.mode === "pct" ? "% du max" : "Charge"}</th>
						<th>RPE cible</th>
					</tr>
				</thead>
				<tbody>
					{exo.series.map((s, sIdx) => (
						<tr key={sIdx}>
							<td className="cell-prog-serie-num">#{sIdx + 1}</td>
							<td>
								<span className="cell-prog-serie-input">
									<input
										type="number"
										min={0}
										max={99}
										value={s.repsCible}
										onChange={(e) => onSerieField(sIdx, "repsCible", e.target.value)}
									/>
									<span className="unit">reps</span>
								</span>
							</td>
							<td>
								{exo.mode === "pct" ? (
									<span className="cell-prog-serie-input">
										<input
											type="number"
											min={0}
											max={200}
											step={2.5}
											value={s.pct}
											onChange={(e) => onSerieField(sIdx, "pct", e.target.value)}
										/>
										<span className="unit">%</span>
									</span>
								) : (
									<span className="cell-prog-serie-input">
										<input
											type="number"
											min={0}
											step={2.5}
											value={s.poidsFixe}
											onChange={(e) => onSerieField(sIdx, "poidsFixe", e.target.value)}
										/>
										<span className="unit">kg</span>
									</span>
								)}
							</td>
							<td>
								<select
									className="cell-prog-serie-rpe"
									value={s.rpeCible || ""}
									onChange={(e) => onSerieField(sIdx, "rpeCible", e.target.value)}
								>
									<option value="">—</option>
									{RPE_VALUES.filter((v) => v != null).map((v) => (
										<option key={v} value={String(v)}>{v}</option>
									))}
								</select>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
